import { parseProject, type Project, type ProjectId } from '@finsim/model';
import { createEffect, createEvent, sample } from 'effector';
import { loadProjectFx, saveProjectFx } from '../../entities/library';
import { toastShown } from '../../entities/notifications';
import { newId } from '../../shared/lib/id';

/*
 * JSON export/import of whole projects (docs/PLAN.md M7): a portable backup that works without an
 * account. Imports are validated and migrated like any stored project and get a new id.
 */

export const exportRequested = createEvent<string>();
export const importFileChosen = createEvent<File>();

const download = (project: Project): void => {
  const blob = new Blob([JSON.stringify(project, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${project.name.replace(/[\\/:*?"<>|]+/g, '-')}.finsim.json`;
  a.click();
  URL.revokeObjectURL(url);
};

export const exportFx = createEffect(async (id: string) => {
  download(await loadProjectFx(id));
});

export const importFx = createEffect(async (file: File): Promise<Project> => {
  let raw: unknown;
  try {
    raw = JSON.parse(await file.text()) as unknown;
  } catch {
    throw new Error('notJson');
  }
  const parsed = parseProject(raw);
  if (!parsed.success) throw new Error(parsed.error);
  return { ...parsed.data, id: newId('prj') as ProjectId };
});

sample({ clock: exportRequested, target: exportFx });
sample({ clock: importFileChosen, target: importFx });
sample({ clock: importFx.doneData, target: saveProjectFx });
sample({
  clock: importFx.doneData,
  fn: (project) => ({ message: 'home.imported' as const, params: { name: project.name } }),
  target: toastShown,
});
sample({
  clock: importFx.fail,
  fn: () => ({ message: 'home.importFailed' as const, tone: 'error' as const }),
  target: toastShown,
});
